import { useEffect, useRef, useState } from 'react';
import type { ApiClient, MemberRole, MemberSummary } from '../api/client';
import { isApiError } from '../api/errors';
import { toDisplayError, type DisplayError } from '../api/errorMessages';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { SelectField } from '../components/SelectField';
import { ROLE_LABELS, ROLE_ORDER, roleLabel } from '../org/roleLabels';
import styles from './Members.module.css';

const ROLE_OPTIONS = ROLE_ORDER.map((role) => ({ value: role, label: ROLE_LABELS[role] }));

export interface MemberRowProps {
  client: ApiClient;
  orgId: string;
  member: MemberSummary;
  /** Sync a role change back into the parent list (persisted value). */
  onRoleUpdated: (updated: MemberSummary) => void;
  /** Ask the parent to drop this member and manage post-removal focus. */
  onRemoved: (userId: string) => void;
  /** Bubble a server 403 to the top-level assertive alert. */
  onForbidden: (display: DisplayError) => void;
  /** Register/deregister the Remove trigger so the parent can move focus to it. */
  registerRemoveRef: (userId: string, el: HTMLButtonElement | null) => void;
}

/**
 * A single member's row: identity + a role SelectField (with a render-only
 * "Update role" action that appears when the selection differs from the
 * persisted role) + a two-step inline remove confirm. Row-scoped 409/404/success
 * are announced in the row's OWN polite live region; a 403 bubbles to the parent
 * assertive alert. Every control carries a member-scoped accessible name.
 */
export function MemberRow({
  client,
  orgId,
  member,
  onRoleUpdated,
  onRemoved,
  onForbidden,
  registerRemoveRef,
}: MemberRowProps) {
  const who = member.email ?? member.userId;
  const [selectedRole, setSelectedRole] = useState<MemberRole>(member.role);
  const [rowMessage, setRowMessage] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const removeRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const prevConfirming = useRef(false);

  // Persisted role changed (e.g. after a successful update) → drop any local
  // selection so the "Update role" action disappears.
  useEffect(() => {
    setSelectedRole(member.role);
  }, [member.role]);

  // Two-step focus: opening moves focus to Confirm; closing (cancel/409) returns
  // it to the Remove trigger. A successful delete unmounts the row (no focus here).
  useEffect(() => {
    if (confirming && !prevConfirming.current) {
      confirmRef.current?.focus();
    } else if (!confirming && prevConfirming.current) {
      removeRef.current?.focus();
    }
    prevConfirming.current = confirming;
  }, [confirming]);

  const dirty = selectedRole !== member.role;

  const updateRole = async () => {
    if (busy) return;
    setBusy(true);
    setRowMessage(null);
    try {
      const updated = await client.updateMemberRole(orgId, member.userId, selectedRole);
      onRoleUpdated(updated);
      setRowMessage(`Role updated to ${roleLabel(updated.role)}.`);
    } catch (err) {
      if (isApiError(err) && err.status === 403) {
        onForbidden(toDisplayError(err));
      } else if (isApiError(err) && err.status === 409) {
        setSelectedRole(member.role); // revert to persisted
        setRowMessage('Cannot demote the last org admin. Role reverted.');
      } else if (isApiError(err) && err.status === 404) {
        setRowMessage('This member no longer exists.');
      } else {
        setRowMessage(toDisplayError(err).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (busy) return;
    setBusy(true);
    setRowMessage(null);
    try {
      await client.removeMember(orgId, member.userId);
      // Row unmounts; the parent syncs the list and moves focus.
      onRemoved(member.userId);
    } catch (err) {
      setBusy(false);
      if (isApiError(err) && err.status === 403) {
        setConfirming(false);
        onForbidden(toDisplayError(err));
      } else if (isApiError(err) && err.status === 409) {
        setConfirming(false);
        setRowMessage('Cannot remove the last org admin.');
      } else if (isApiError(err) && err.status === 404) {
        setConfirming(false);
        setRowMessage('This member no longer exists.');
      } else {
        setConfirming(false);
        setRowMessage(toDisplayError(err).message);
      }
    }
  };

  return (
    <li className={styles.row}>
      <div className={styles.identity}>
        {member.email ? (
          <span className={styles.email}>{member.email}</span>
        ) : (
          <span className={styles.muted} aria-label="Email hidden">
            —
          </span>
        )}
      </div>

      <div className={styles.roleControls}>
        <div className={styles.roleSelect}>
          <SelectField
            label="Role"
            id={`role-${member.userId}`}
            value={selectedRole}
            onChange={(v) => setSelectedRole(v as MemberRole)}
            options={ROLE_OPTIONS}
            aria-label={`Role for ${who}`}
          />
        </div>
        {dirty ? (
          <Button
            variant="secondary"
            busy={busy}
            aria-label={`Update role for ${who}`}
            onClick={() => void updateRole()}
          >
            Update role
          </Button>
        ) : null}
      </div>

      <div className={styles.removeControls}>
        {confirming ? (
          <>
            <Button
              ref={confirmRef}
              variant="danger"
              busy={busy}
              aria-label={`Confirm removing ${who}`}
              onClick={() => void confirmRemove()}
            >
              Confirm
            </Button>
            <Button
              variant="ghost"
              aria-label={`Cancel removing ${who}`}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            ref={(el) => {
              removeRef.current = el;
              registerRemoveRef(member.userId, el);
            }}
            variant="ghost"
            aria-label={`Remove ${who}`}
            onClick={() => setConfirming(true)}
          >
            Remove
          </Button>
        )}
      </div>

      <div className={styles.rowStatus}>
        <InlineAlert severity="info">{rowMessage}</InlineAlert>
      </div>
    </li>
  );
}
